import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { screenLang } from './screenLang';

/**
 * Real product screenshots for the landing pages (generated by
 * `npm run screenshots:marketing` from the fictional demo gym), one file per
 * language: /images/screens/<set>/<name>.<lang>.webp plus -480w/-800w/-1200w.
 */

interface LocalizedScreenshotProps {
  set: string;
  name: string;
  alt: string;
  sizes: string;
  className?: string;
  /** Crop focus for object-position (phones show the top of the screen). */
  position?: string;
}

export function LocalizedScreenshot({ set, name, alt, sizes, className, position = 'top' }: LocalizedScreenshotProps) {
  const { i18n } = useTranslation();
  const base = `/images/screens/${set}/${name}.${screenLang(i18n.language)}`;
  return (
    <img
      src={`${base}.webp`}
      srcSet={`${base}-480w.webp 480w, ${base}-800w.webp 800w, ${base}-1200w.webp 1200w`}
      sizes={sizes}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={cn('block h-full w-full object-cover', className)}
      style={{ objectPosition: position }}
    />
  );
}

/**
 * A phone around a screenshot, drawn in CSS so the image stays crisp and translatable.
 * `h-fit`: in a flex row the frame would otherwise stretch to its tallest sibling
 * and leave an empty black band under the screen.
 */
export function PhoneFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('h-fit shrink-0 rounded-[40px] bg-ink p-2.5 shadow-hero', className)}>
      <div className="aspect-[390/780] overflow-hidden rounded-[31px] bg-bg-public">{children}</div>
    </div>
  );
}

/** A minimal browser window around a desktop screenshot. */
export function BrowserFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-line bg-surface shadow-hero', className)}>
      <div className="flex h-8 items-center gap-1.5 border-b border-line bg-surface-2 px-3" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
      </div>
      <div className="aspect-[1440/860] overflow-hidden">{children}</div>
    </div>
  );
}
